// ===================================================================
// PA GRANTS — Damage Inventory
// Full CRUD for damaged facilities/sites with filtering
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh, IconDownload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { exportToCSV } from '../../lib/exportUtils';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useDisasterFilter } from '../../hooks/useActiveDisaster';
import type { PADamageItem, DamageCategory } from '../../types/pagrants';
import { DAMAGE_CATEGORY_LABELS, DAMAGE_CATEGORY_COLORS } from '../../types/pagrants';
import { WORKER_URL } from '../../lib/api-switch';

const WORKER = WORKER_URL;

// ── Constants ──────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'identified', label: 'Identified' },
  { value: 'inspected', label: 'Inspected' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'eligible', label: 'Eligible' },
  { value: 'ineligible', label: 'Ineligible' },
];

const STATUS_COLORS: Record<string, string> = {
  identified: 'blue', inspected: 'yellow', assigned: 'violet', eligible: 'green', ineligible: 'red',
};

const CATEGORY_OPTIONS = (Object.keys(DAMAGE_CATEGORY_LABELS) as DamageCategory[]).map(k => ({
  value: k, label: DAMAGE_CATEGORY_LABELS[k],
}));

const FACILITY_TYPE_OPTIONS = [
  { value: 'building', label: 'Building' },
  { value: 'road', label: 'Road' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'utility', label: 'Utility' },
  { value: 'park', label: 'Park' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'major', label: 'Major' },
  { value: 'destroyed', label: 'Destroyed' },
];

const EMPTY: Partial<PADamageItem> = {
  facilityName: '', facilityType: '', category: '',
  siteAddress: '', siteCity: '', siteCounty: '', siteState: '', siteZip: '',
  lat: '', lng: '',
  preDisasterCondition: '', preDisasterUse: '',
  damageDescription: '', damageCause: '', damageSeverity: '',
  estimatedCost: '', dimensions: '',
  siteInspected: '', siteInspectionDate: '', siteInspectorName: '', siteInspectorOrg: '',
  workStatus: '',
  organizationId: '', disasterId: '', projectId: '',
  status: 'identified', ineligibleReason: '', notes: '',
};

const fmt = (v: string) => {
  const n = parseFloat(v);
  return isNaN(n) ? '—' : `$${Number(n).toLocaleString()}`;
};

const thStyle: React.CSSProperties = { padding: '8px 12px', textAlign: 'left' };
const tdStyle: React.CSSProperties = { padding: '8px 12px' };
const tdRight: React.CSSProperties = { padding: '8px 12px', textAlign: 'right' };

// ── Component ──────────────────────────────────────────────────

export default function DamageInventory() {
  const {
    damageItems: allDamageItems, organizations, disasters, projects,
    loadingDamage,
    fetchAll, fetchDamageItems,
    createDamageItem, updateDamageItem, deleteDamageItem,
  } = usePAGrantsStore();
  const damageItems = useDisasterFilter(allDamageItems);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PADamageItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PADamageItem | null>(null);
  const [form, setForm] = useState<Partial<PADamageItem>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  // Address autocomplete
  const [addrResults, setAddrResults] = useState<Array<{ label: string; city: string; state: string; zip: string; lat: string; lng: string }>>([]);
  const [addrTimer] = useState({ current: null as ReturnType<typeof setTimeout> | null });
  const fetchAddr = (q: string) => {
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (q.length < 3) { setAddrResults([]); return; }
    addrTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${WORKER}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`, {
          headers: { 'Origin': 'https://apps.opengov.com' },
        });
        if (!r.ok) return;
        const data = await r.json();
        setAddrResults((data.items || []).filter((i: any) => i.address).map((i: any) => ({
          label: i.address?.label || i.title || '',
          city: i.address?.city || '',
          state: i.address?.stateCode || i.address?.state || '',
          zip: i.address?.postalCode || '',
          lat: i.position ? String(i.position.lat) : '',
          lng: i.position ? String(i.position.lng) : '',
        })));
      } catch { setAddrResults([]); }
    }, 250);
  };

  // Load all data on mount so lookups are available
  useEffect(() => {
    if (organizations.length === 0 || disasters.length === 0 || projects.length === 0) {
      fetchAll();
    } else {
      fetchDamageItems();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build select data arrays from related entities
  const orgSelectData = useMemo(
    () => organizations.map(o => ({ value: String(o.id), label: o.orgName || `Org #${o.id}` })),
    [organizations],
  );

  const disasterSelectData = useMemo(
    () => disasters.map(d => ({ value: String(d.id), label: d.disasterName || `Disaster #${d.id}` })),
    [disasters],
  );

  const projectSelectData = useMemo(
    () => projects.map(p => ({ value: String(p.id), label: p.projectTitle || p.projectNumber || `Project #${p.id}` })),
    [projects],
  );

  // Org lookup map for table display
  const orgMap = useMemo(
    () => new Map(organizations.map(o => [String(o.id), o.orgName])),
    [organizations],
  );

  // Filtered list
  const filtered = useMemo(() => {
    let list = damageItems;
    if (categoryFilter && categoryFilter !== 'all') {
      list = list.filter(d => d.category === categoryFilter);
    }
    if (statusFilter && statusFilter !== 'all') {
      list = list.filter(d => d.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.facilityName.toLowerCase().includes(q) ||
        d.facilityType.toLowerCase().includes(q) ||
        d.siteCity.toLowerCase().includes(q) ||
        d.damageDescription.toLowerCase().includes(q) ||
        (orgMap.get(d.organizationId) ?? '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return a.facilityName.localeCompare(b.facilityName);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const numA = Number(va); const numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB) && va !== '' && vb !== '') return (numA - numB) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [damageItems, categoryFilter, statusFilter, search, orgMap, sortField, sortDir]);

  const setField = (key: keyof PADamageItem, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setModalOpen(true); };
  const openEdit = (item: PADamageItem) => { setEditTarget(item); setForm({ ...item }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.facilityName?.trim()) {
      notifications.show({ message: 'Facility name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await updateDamageItem(editTarget.id, form);
        notifications.show({ message: 'Damage item updated', color: 'green' });
      } else {
        await createDamageItem(form);
        notifications.show({ message: 'Damage item created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteDamageItem(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.facilityName} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Damage Inventory</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconDownload size={16} />} onClick={() => {
            exportToCSV('pa-damage-inventory.csv',
              ['Facility', 'Category', 'Status', 'Address', 'County', 'Estimated Cost'],
              filtered.map(d => [d.facilityName, d.category, d.status, [d.siteAddress, d.siteCity, d.siteState].filter(Boolean).join(', '), d.siteCounty, d.estimatedCost])
            );
          }}>Export CSV</Button>
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingDamage} onClick={() => fetchDamageItems()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Item</Button>
        </Group>
      </Group>

      {/* Filter bar */}
      <Group mb="md" gap="sm">
        <TextInput placeholder="Search facility, city, org..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Categories' }, ...CATEGORY_OPTIONS]}
          value={categoryFilter ?? 'all'} onChange={v => setCategoryFilter(v)} size="xs" w={220} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</Text>

      {/* Table */}
      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('facilityName')}>Facility Name{sortIcon('facilityName')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('category')}>Category{sortIcon('category')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('facilityType')}>Facility Type{sortIcon('facilityType')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('siteCity')}>City / State{sortIcon('siteCity')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('severity')}>Severity{sortIcon('severity')}</th>
                <th style={{ ...thStyle, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('estimatedCost')}>Est. Cost{sortIcon('estimatedCost')}</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>
                <th style={thStyle}>Organization</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingDamage ? 'Loading...' : 'No damage items found'}
                </td></tr>
              )}
              {filtered.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{item.facilityName || '—'}</td>
                  <td style={tdStyle}>
                    {item.category ? (
                      <Badge size="sm" style={{
                        backgroundColor: DAMAGE_CATEGORY_COLORS[item.category as DamageCategory] ?? '#888',
                        color: '#fff',
                      }}>
                        {DAMAGE_CATEGORY_LABELS[item.category as DamageCategory] ?? item.category}
                      </Badge>
                    ) : '—'}
                  </td>
                  <td style={tdStyle}>{item.facilityType || '—'}</td>
                  <td style={tdStyle}>
                    {item.siteCity || item.siteState
                      ? `${item.siteCity}${item.siteCity && item.siteState ? ', ' : ''}${item.siteState}`
                      : '—'}
                  </td>
                  <td style={tdStyle}>{item.damageSeverity || '—'}</td>
                  <td style={tdRight}>{fmt(item.estimatedCost)}</td>
                  <td style={tdStyle}>
                    <Badge size="sm" color={STATUS_COLORS[item.status] ?? 'gray'}>{item.status}</Badge>
                  </td>
                  <td style={tdStyle}>{orgMap.get(item.organizationId) ?? '—'}</td>
                  <td style={tdRight}>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(item)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(item)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* ── Create / Edit Modal ──────────────────────────────── */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Damage Item' : 'Add Damage Item'} centered size="xl">
        <Stack>
          {/* Facility Info */}
          <Text size="sm" fw={600} c="dimmed">Facility Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Facility Name" required value={form.facilityName ?? ''}
              onChange={e => setField('facilityName', e.currentTarget.value)} />
            <Select label="Facility Type" data={FACILITY_TYPE_OPTIONS}
              value={form.facilityType || null}
              onChange={v => setField('facilityType', v ?? '')} clearable />
            <Select label="Category" data={CATEGORY_OPTIONS}
              value={form.category || null}
              onChange={v => setField('category', v ?? '')} clearable />
          </SimpleGrid>

          {/* Location */}
          <Text size="sm" fw={600} c="dimmed" mt="sm">Location</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <div style={{ position: 'relative' }}>
              <TextInput label="Street Address" value={form.siteAddress ?? ''}
                onChange={e => { setField('siteAddress', e.currentTarget.value); fetchAddr(e.currentTarget.value); }}
                onBlur={() => setTimeout(() => setAddrResults([]), 200)} />
              {addrResults.length > 0 && (
                <Paper withBorder shadow="md" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, maxHeight: 180, overflowY: 'auto' }}>
                  {addrResults.map((r, i) => (
                    <div key={i} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}
                      onMouseDown={() => {
                        setForm(prev => ({
                          ...prev,
                          siteAddress: r.label.split(',')[0] || r.label,
                          siteCity: r.city,
                          siteState: r.state,
                          siteZip: r.zip,
                          lat: r.lat,
                          lng: r.lng,
                        }));
                        setAddrResults([]);
                      }}>
                      {r.label}
                    </div>
                  ))}
                </Paper>
              )}
            </div>
            <TextInput label="City" value={form.siteCity ?? ''}
              onChange={e => setField('siteCity', e.currentTarget.value)} />
            <TextInput label="County" value={form.siteCounty ?? ''}
              onChange={e => setField('siteCounty', e.currentTarget.value)} />
            <TextInput label="State" value={form.siteState ?? ''}
              onChange={e => setField('siteState', e.currentTarget.value)} />
            <TextInput label="ZIP" value={form.siteZip ?? ''}
              onChange={e => setField('siteZip', e.currentTarget.value)} />
            <SimpleGrid cols={2} spacing="sm">
              <TextInput label="Lat" value={form.lat ?? ''}
                onChange={e => setField('lat', e.currentTarget.value)} />
              <TextInput label="Lng" value={form.lng ?? ''}
                onChange={e => setField('lng', e.currentTarget.value)} />
            </SimpleGrid>
          </SimpleGrid>

          {/* Damage Details */}
          <Text size="sm" fw={600} c="dimmed" mt="sm">Damage Details</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Textarea label="Pre-Disaster Condition" value={form.preDisasterCondition ?? ''}
              onChange={e => setField('preDisasterCondition', e.currentTarget.value)} autosize minRows={2} />
            <TextInput label="Pre-Disaster Use" value={form.preDisasterUse ?? ''}
              onChange={e => setField('preDisasterUse', e.currentTarget.value)} />
            <Textarea label="Damage Description" value={form.damageDescription ?? ''}
              onChange={e => setField('damageDescription', e.currentTarget.value)} autosize minRows={2} />
            <TextInput label="Damage Cause" value={form.damageCause ?? ''}
              onChange={e => setField('damageCause', e.currentTarget.value)} />
            <Select label="Damage Severity" data={SEVERITY_OPTIONS}
              value={form.damageSeverity || null}
              onChange={v => setField('damageSeverity', v ?? '')} clearable />
            <TextInput label="Estimated Cost ($)" value={form.estimatedCost ?? ''}
              onChange={e => setField('estimatedCost', e.currentTarget.value)} />
            <TextInput label="Dimensions" value={form.dimensions ?? ''}
              onChange={e => setField('dimensions', e.currentTarget.value)} />
          </SimpleGrid>

          {/* Inspection */}
          <Text size="sm" fw={600} c="dimmed" mt="sm">Inspection</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Select label="Site Inspected" data={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
              value={form.siteInspected || null}
              onChange={v => setField('siteInspected', v ?? '')} clearable />
            <TextInput label="Inspection Date" type="date" value={form.siteInspectionDate ?? ''}
              onChange={e => setField('siteInspectionDate', e.currentTarget.value)} />
            <TextInput label="Inspector Name" value={form.siteInspectorName ?? ''}
              onChange={e => setField('siteInspectorName', e.currentTarget.value)} />
            <TextInput label="Inspector Org" value={form.siteInspectorOrg ?? ''}
              onChange={e => setField('siteInspectorOrg', e.currentTarget.value)} />
            <TextInput label="Work Status" value={form.workStatus ?? ''}
              onChange={e => setField('workStatus', e.currentTarget.value)} />
          </SimpleGrid>

          {/* Assignment */}
          <Text size="sm" fw={600} c="dimmed" mt="sm">Assignment</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Select label="Organization" data={orgSelectData} searchable
              value={form.organizationId || null}
              onChange={v => setField('organizationId', v ?? '')} clearable />
            <Select label="Disaster" data={disasterSelectData} searchable
              value={form.disasterId || null}
              onChange={v => setField('disasterId', v ?? '')} clearable />
            <Select label="Project (optional)" data={projectSelectData} searchable
              value={form.projectId || null}
              onChange={v => setField('projectId', v ?? '')} clearable />
          </SimpleGrid>

          {/* Status */}
          <Text size="sm" fw={600} c="dimmed" mt="sm">Status</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Status" data={STATUS_OPTIONS}
              value={form.status || 'identified'}
              onChange={v => setField('status', v ?? 'identified')} />
            {form.status === 'ineligible' && (
              <TextInput label="Ineligible Reason" value={form.ineligibleReason ?? ''}
                onChange={e => setField('ineligibleReason', e.currentTarget.value)} />
            )}
          </SimpleGrid>
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Item'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete Confirmation ──────────────────────────────── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Damage Item" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.facilityName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
